import { useEffect, useRef } from 'react';

export function Modal({ open, onClose, title, children, footer }) {
  const dialogRef = useRef(null);

  // Focus the dialog once when it opens. This must depend on `open` alone —
  // not on `onClose` — otherwise every re-render of the parent (e.g. typing
  // in a form field, which updates state on every keystroke) recreates the
  // inline onClose function, re-runs this effect, and calls .focus() again,
  // yanking focus away from whatever input the user was just typing in.
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
  }, [open]);

  // Close on Escape — safe to depend on onClose since it only adds/removes
  // a document listener and never touches focus.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal" ref={dialogRef} tabIndex={-1}>
        <div className="modal-header">
          <h2 className="modal-title" id="modal-title">{title}</h2>
          <button className="btn-icon" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
